<template>
  <div class="required-actions-paies-page">
    <div class="required-actions-paies-page__main">

      <layout-nav-tab-menu
        v-model="navSection"
        :items="navSectionOptions"
        class="required-actions-paies-page__nav"
        badge-animation
        badge-placeholder
      />

      <!-- FILTERS -->
      <div class="d-flex justify-content-space-between align-items-flex-start gap-10 p-2">
        <ged-paies-list-filters
          :class="[
            isConsumerPending ? 'required-actions-paies-page__filters--busy' : '',
            'd-flex flex-grow-1 flew-wrap flex-basis-0'
          ]"
          v-bind="{
            filters: context.filters,
            updateFiltersCallback: setFilters,
            loadSalarieOptionsCallback: loadSalarieOptions,
            resetFiltersCallback: resetFilters,
          }"
        />

        <ui-chip-field-management
          v-model="fields"
          :class="[
            isConsumerPending ? 'required-actions-paies-page__filters--busy' : '',
          ]"
        />
      </div>

      <div class="required-actions-paies-page__table-scroll">
        <ui-table-wrapper
          ref="table"
          :fields="fields"
          :provider="provider"
          primary-key="id"
          :size="UI.SIZE.LARGE"
          :initial-sort-by="context.order"
          :initial-sort-desc="context.sort === 'desc'"
          :hide-header="fetchState.status.value !== 'success'"
          hover
          striped
          fixed
        >
          <template v-if="fetchState.status.value !== 'success' || providerCallCount === 0" #table-busy>
            <layout-placeholder-table-body
              class="required-actions-paies-page__placeholder"
              :count="context.perPage"
              :size="UI.SIZE.MEDIUM"
              striped
            />
          </template>

          <template #empty>
            <div class="required-actions-paies-page__empty">
              <ui-icon
                name="search_off"
                class="font-size-40px color-black mb-1"
              />
              <p class="font-weight-600 color-black mb-1 font-family-heading">
                Aucun document trouvé
              </p>
              <p class="color-gray-deep mb-1">
                Nous n'avons trouvé aucun résultat correspondant à votre recherche
              </p>
              <p class="color-gray-dark mb-2">
                Les résultats peuvent aussi être automatiquement filtrés
                <br>
                selon votre accès aux salariés confidentiels
              </p>
              <ui-button
                v-if="hasAnyCriterionSet"
                :variant="UI.VARIANT.LIGHT"
                :size="UI.SIZE.SMALL"
                @click="resetFilters"
              >
                <ui-icon
                  name="replay"
                />&nbsp;Réinitialiser les filtres
            </ui-button>
            </div>
          </template>

          <template #cell(status)="data">
            <ui-badge-heavy
              :class="[
                'required-actions-paies-page__statut-badge',
                `required-actions-paies-page__statut-badge--${data.item.status}`,
              ]"
              variant="light"
            >
              {{ data.value }}
            </ui-badge-heavy>
          </template>

          <template #cell(fullname)="data">
            <ui-tooltip-wrapper
              v-if="data.item.fullname"
              :title="`${data.item.fullname} — ${data.item.matricule}`"
              class="d-block min-w-0"
              placement="top-start"
              no-caret
              no-animation
              :offset="6"
              :delay="100"
            >
              <span class="d-block text-truncate">
                {{ data.item.fullname }}
              </span>
            </ui-tooltip-wrapper>
          </template>
        </ui-table-wrapper>
      </div>

      <div class="required-actions-paies-page__footer">
        <span
          v-hide="context.total === 0"
          class="required-actions-paies-page__footer__count"
        >
          <layout-misc-float :value="context.total" />
          {{ context.total <= 1 ? 'résultat' : 'résultats' }}
        </span>

        <ui-blank
          v-if="context.total === 0"
          class="required-actions-paies-page__footer__blank"
        />

        <ui-table-pagination
          v-model="context.currentPage"
          v-hide="context.total < context.perPage || context.total === 0"
          :total-rows="context.total"
          :per-page="context.perPage"
        />

        <div class="required-actions-paies-page__footer__per-page">
          <ui-form-select
            v-model="context.perPage"
            :size="UI.SIZE.SMALL"
            :options="perPageOptions"
            class="required-actions-paies-page__footer__per-page__select"
          />
          <div class="required-actions-paies-page__footer__per-page__text">
            par page
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="sass" scoped>
@import ~/assets/sass/modules/ged/members
@import ~/assets/sass/modules/layout/members
@import ~/assets/sass/modules/layout/variables
@import ~/assets/sass/modules/table/members
@import '~/assets/sass/abstracts/members'

$code-societe-column-width: 100px
$numero-paie-column-width: 80px
$salarie-column-width: 300px
$date-column-width: 90px
$brut-column-width: 110px
$net-a-payer-column-width: 120px
$net-avant-pas-column-width: 130px
$cout-employeur-column-width: 150px
$contrat-column-width: 145px
$etat-column-width: 80px
$derniere-modif-column-width: 130px
$status-badge-max-width: 100px

.required-actions-paies-page
  $self: &
  height: 100%
  display: flex
  flex-direction: column

  &__main
    height: 100%
    display: flex
    flex-direction: column

  &__nav
    flex-shrink: 0
    padding-top: space-x(2)

  &__filters

    &--busy
      @include no-interaction
      opacity: 0.6
      transition: 0.3s
      transition-delay: 0.3s

  &__table-scroll
    @include always-show-scrollbar()
    contain: size
    overflow-x: scroll
    height: 100%
    flex-grow: 1
    container-type: inline-size

  &__empty
    position: sticky
    left: 0
    width: 100cqw
    display: flex
    flex-direction: column
    align-items: center
    margin: 20vh 0 space-x(2)

  &__footer
    border-top: 1px solid $border-color
    display: flex
    align-items: center
    justify-content: space-between
    padding: ($layout-spacer-y * .25) ($layout-spacer-x * .5)

    &__count
      font-size: 85%

    &__per-page
      display: flex
      align-items: center
      gap: space-x(1)
      flex-shrink: 0

      &__text
        font-weight: $font-weight-light
        font-size: 90%
        white-space: nowrap

  :deep(.b-table.table)
    @include table-body-grow
    @include table-presets

    margin-bottom: 0
    height: 100%

    /// Header chrome, needed on both nodes:
    ///   - `thead`: outlives the `hide-header` state (see <ui-table-wrapper>,
    ///     `visibility` suppressing the element own painting)
    ///   - `th`: travels with the sticky pinning (see below)
    $header-background: $body-bg
    $header-rule: inset 0 -1px 0 $submenu-border-color, inset 0 1px 0 $submenu-border-color

    thead
      background-color: $header-background
      box-shadow: $header-rule

    @include table-header-small
      background-color: $header-background
      box-shadow: $header-rule
      font-size: 13.5px

    #{ $self }__statut
      width: $etat-column-width

    #{ $self }__statut-badge
      @include paies-status-badge()
      max-width: $status-badge-max-width
      width: 100%

    #{ $self }__code-societe
      width: $code-societe-column-width

    #{ $self }__numero-paie
      width: $numero-paie-column-width

    #{ $self }__salarie
      max-width: 100%
      width: $salarie-column-width

    #{ $self }__contrat
      text-align: center
      width: $contrat-column-width

    #{ $self }__date
      text-align: left
      width: $date-column-width

    #{ $self }__brut
      white-space: nowrap
      width: $brut-column-width
      text-align: right

    #{ $self }__net-a-payer
      width: $net-a-payer-column-width
      white-space: nowrap
      text-align: right

    #{ $self }__net-avant-pas
      width: $net-avant-pas-column-width
      white-space: nowrap
      text-align: right

    #{ $self }__cout-employeur
      width: $cout-employeur-column-width
      text-align: right

    #{ $self }__derniere-modif
      width: $derniere-modif-column-width
      text-align: center

  /// Animation
  ///

  &__filters
    animation: fade-in 150ms ease-in-out

  &__empty
    animation: fade-in 50ms ease-in

  /// Grace delay before showing the loading rows: a fast enough consumer
  /// unmounts them before they ever get visible (avoiding a flicker whenever
  /// the result set is systematically empty)
  &__placeholder
    opacity: 0
    animation: fade-in 300ms ease-in 150ms forwards

</style>

<script lang="ts">
import {
  LayoutNavTabMenu,
  LayoutPlaceholderTableBody,
  type UiTableWrapper,
} from '#components'
import {
  useCompanyOptionalNavigationGuard,
} from '~/composables/layout/navigation'
import {
  useTableProvider,
  type Config as TableConfig,
  type DefaultData as ProviderDefaultData
} from '~/composables/layout/table-provider'
import { useToastBuilder } from '~/composables/layout/toast'
import {
  getDefaultFilters,
  printStatus,
  useStore_ged_paies_list,
  type GedPaie,
  type GedPaiesListFilters
} from '~/stores/ged/paies/list'
import { wrap as wrapInput } from '~/utils/infra/common/types'
import { parseDatetime } from '~/utils/lib/core/datetime'
import { toString as floatToString } from '~/utils/lib/core/float'
import { assoc, clone, pipe_, tapCatch, toString } from '~/utils/lib/core/prelude'
import { MENU_ITEM, NAVIGATION_SCOPE } from '~/utils/lib/layout/types'
import { useStore_ged_salarie } from '~/stores/ged/salarie'
import Console from '~/utils/lib/core/console'
import type { ComponentExposed } from 'vue-component-type-helpers'
import { TypeContrat } from '@ghsfr/paie-lib'
import { useStore_account_details } from '~/stores/account/details'
import type { TableField } from '~/components/ui/table/Wrapper.vue'
import { useLegacyPageFetch } from '~/composables/core/hook'

const console = new Console('page:actions-requises-paies-list')

function formatDate(date: string | undefined): string {
  if (!date) return ''
  return pipe_(
    date,
    parseDatetime({
      inputFormat: undefined,
      outputFormat: 'DD/MM/YYYY',
    }),
  )
}

function formatToDecimal(value): string {
  if (value === undefined) return ''
  return floatToString(Number(value), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function formatType(value: TypeContrat): string {
  switch (value) {
    case TypeContrat.INTERMITTENT:
      return 'Int.'
    case TypeContrat.CDD:
      return 'CDD'
    case TypeContrat.CDI:
      return 'CDI'
    default:
      return ''
  }
}

export default defineComponent({
  name: 'RequiredActionsPaiesPage',
  components: {
    LayoutNavTabMenu,
    LayoutPlaceholderTableBody,
  },
  setup() {
    definePageMeta({
      navigationScope: NAVIGATION_SCOPE.COMPANY_OPTIONAL,
      menuItem: MENU_ITEM.ACTIONS_REQUISES,
    })
    useHead({
      title: 'Actions requises — Hamac',
    })
    const navigationContext = useCompanyOptionalNavigationGuard()
    const ToastBuilder = useToastBuilder()
    const store_account_details = useStore_account_details()
    const store_paies_list = useStore_ged_paies_list()
    const store_salaries = useStore_ged_salarie()

    const ref_table
      = useTemplateRef<ComponentExposed<typeof UiTableWrapper>>('table')
    const tableConfig :TableConfig = {
      precedingSorting: {
        sort: 'desc',
        order: 'last_modification',
      },
    }
    const providerDefaultData :ProviderDefaultData = {
      context: {
        filters: getDefaultFilters(),
        perPage: 30,
        order: 'last_modification',
        sort: 'desc',
      },
    }
    const tableProvider = useTableProvider<GedPaie, GedPaiesListFilters>(
      ref_table,
      providerDefaultData,
      tableConfig
    )

    const {
      fetchState,
      fetchWrapper,
    } = useLegacyPageFetch()

    const navSection = ref<string>('paies')

    return {
      fetchState,
      fetchWrapper,
      navSection,
      ...navigationContext,
      store_account_details,
      store_paies_list,
      store_salaries,
      ...tableProvider,
      ToastBuilder,
    }
  },
  data() {
    return {
      navSectionOptions: [
        {
          id: 'paies',
          text: 'Paies',
        },
      ],
      fields: [
        {
          key: 'status',
          label: 'Statut',
          class: 'required-actions-paies-page__statut',
          formatter: ({ value }) => printStatus(value as GedPaie['status']),
          displayed: true,
          manageable: true,
        },
        {
          key: 'code_societe',
          label: 'Code société',
          class: 'required-actions-paies-page__code-societe',
          displayed: true,
          manageable: true,
        },
        {
          key: 'numero_paie',
          label: 'N° paie',
          class: 'required-actions-paies-page__numero-paie',
          displayed: true,
          manageable: true,
        },
        {
          key: 'fullname',
          label: 'Nom salarié',
          class: 'required-actions-paies-page__salarie',
          displayed: true,
          manageable: true,
          sortable: true,
        },
        {
          key: 'type',
          label: 'Type',
          class: 'required-actions-paies-page__contrat',
          formatter: ({ value }) => formatType(value as TypeContrat),
          displayed: true,
          manageable: true,
        },
        {
          key: 'date_debut',
          label: 'Du',
          class: 'required-actions-paies-page__date',
          formatter: ({ value }) => formatDate(value as string | undefined),
          displayed: true,
          manageable: true,
        },
        {
          key: 'date_fin',
          label: 'Au',
          class: 'required-actions-paies-page__date',
          formatter: ({ value }) => formatDate(value as string | undefined),
          displayed: true,
          manageable: true,
        },
        {
          key: 'montant_brut',
          label: 'Brut',
          class: 'required-actions-paies-page__brut',
          formatter: ({ value }) => formatToDecimal(value),
          displayed: true,
          manageable: true,
          sortable: true,
        },
        {
          key: 'montant_net',
          label: 'Net à payer',
          class: 'required-actions-paies-page__net-a-payer',
          formatter: ({ value }) => formatToDecimal(value),
          displayed: true,
          manageable: true,
          sortable: true,
        },
        {
          key: 'montant_net_avant_pas',
          label: 'Net av. PAS',
          class: 'required-actions-paies-page__net-avant-pas',
          formatter: ({ value }) => formatToDecimal(value),
          displayed: true,
          manageable: true,
          sortable: true,
        },
        {
          key: 'montant_employeur',
          label: 'Coût employeurs',
          class: 'required-actions-paies-page__cout-employeur',
          formatter: ({ value }) => formatToDecimal(value),
          displayed: true,
          manageable: true,
          sortable: true,
        },
        {
          key: 'last_modification',
          label: 'Dernière modif.',
          class: 'required-actions-paies-page__derniere-modif',
          formatter: ({ value }) => formatDate(value as string | undefined),
          displayed: true,
          manageable: true,
          sortable: true,
        },
      ] as TableField[],
      perPageOptions: [
        { value: 30, text: '30' },
        { value: 50, text: '50' },
        { value: 100, text: '100' },
      ],
    }
  },
  watch: {
    'context.total'() {
      this.navSectionOptions[0] = assoc(
        'badgeCount',
        this.context.total,
        this.navSectionOptions[0]
      )
    },
    fields: {
      deep: true,
      handler() {
        if (this.fetchState.status.value === 'pending')
          return

        this.savePreferences()
      },
    },
  },
  created() {
    this.fetchWrapper(
      () => this.loadPreferences()
    )
    this.consumer = (context) => {
      const {
        structure: { uuidData },
        company_code_list,
      } = this.getGedScope()

      const input = wrapInput({
        ...context,
        filters: {
          ...context.filters,
          code_societes: company_code_list,
        },
        currentPage: context.currentPage,
        perPage: context.perPage,
      })

      return this.store_paies_list.exec({
        uuidData,
        input
      })
    }
    this.resetFilters()
  },
  methods: {
    getGedScope() {
      const currentLevel = this.fromCurrentLevel()
      const structure = currentLevel.structure

      switch (currentLevel.kind) {
        case 'COMPANY':
          return {
            company_code_list: [ currentLevel.company.code_societe ],
            current_company_code: currentLevel.company.code_societe,
            structure,
          }
        case 'STRUCTURE':
          return {
            company_code_list: Object.keys(structure.societes ?? {}),
            current_company_code: '',
            structure,
          }
      }
    },
    loadPreferences() {
      const { structure: { uuidData } } = this.fromCurrentLevel()
      const options = {
        uuidData,
        id: this.store_account_details.id,
        fields: clone(this.fields)
      }

      return this.store_paies_list.loadPreferences(options)
        .then((fields) => { this.fields = fields })
        .catch(tapCatch((err) => {
          console.warn('loadPreferences', err)
          this.ToastBuilder
            .error(toString(err))
            .title('Erreur lors de la récupération de l\'affichage des colonnes')
            .show()
        }))
    },
    savePreferences() {
      const { structure: { uuidData } } = this.fromCurrentLevel()

      return this.store_paies_list.savePreferences({
          uuidData,
          id: this.store_account_details.id,
          fields: clone(this.fields)
        })
        .catch((err) => {
          console.warn('savePreferences', err)
          this.ToastBuilder
            .error(toString(err))
            .title('Erreur lors de l\'enregistrement de l\'affichage des colonnes')
            .show()
        })
    },
    loadSalarieOptions(search: string) {
      const { company_code_list, structure: { uuidData }, } = this.getGedScope()
      const options = {
        uuidData,
        search,
        code_societes: company_code_list,
      }

      return this.store_salaries.search(options)
        .catch((err) => {
          console.warn('loadSalarieOptions', err)
          this.ToastBuilder
            .error(toString(err))
            .title('Erreur lors de la recherche des salariés')
            .show()
          return []
        })
    },
    resetFilters() {
      this.store_salaries.$patch({ selectedSalaries: []})
      this.resetCriterion()
    },
  },
})
</script>
